/**
 * 租户服务
 * 对接后端租户相关 API
 */

import { request } from './client';

/**
 * 搜索租户（登录页面使用）
 * 为保护隐私，只有输入准确的租户名称或代码才能找到对应租户
 * @param {string} keyword - 搜索关键词（租户名称或代码）
 * @returns {Promise<Array>} 匹配的租户列表
 */
export const searchTenants = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        return [];
    }
    const trimmedKeyword = keyword.trim();
    // 注意：租户搜索接口路径是 /api/label/tenant/search，不是 /api/foodlabel 下的接口
    // 需要使用 skipBaseUrl 并指定完整路径
    const result = await request('/api/label/tenant/search', {
        method: 'GET',
        params: { keyword: trimmedKeyword },
        skipAuth: true,  // 登录前调用，不需要认证
        skipTenant: true, // 不需要租户头
        skipBaseUrl: true, // 跳过默认 base URL，使用完整路径
    });
    const list = result?.list || result;
    return Array.isArray(list) ? list : [];
};

/**
 * 【安全修复】获取当前租户 ID
 * 只从 localStorage 获取，不再从 URL 参数获取
 * URL 参数方式存在安全风险：攻击者可通过构造恶意链接篡改用户的租户上下文
 * 如需切换租户，应使用 switchTenantWithValidation 方法（会调用后端验证）
 */
export const getCurrentTenantId = () => {
    return localStorage.getItem('current_tenant_id');
};

/**
 * 【安全修复】获取当前企业 ID
 * 只从 localStorage 获取，不再从 URL 参数获取
 */
export const getCurrentEnterpriseId = () => {
    return localStorage.getItem('current_enterprise_id');
};

// 设置当前租户 ID
export const setCurrentTenantId = (tenantId) => {
    if (tenantId) {
        localStorage.setItem('current_tenant_id', tenantId);
    } else {
        localStorage.removeItem('current_tenant_id');
    }
};

// 设置当前企业 ID
export const setCurrentEnterpriseId = (enterpriseId) => {
    if (enterpriseId) {
        localStorage.setItem('current_enterprise_id', enterpriseId);
    } else {
        localStorage.removeItem('current_enterprise_id');
    }
};

/**
 * 获取租户页面配置
 * 用于动态加载租户的 Logo、主题色等
 */
export const getTenantPageConfig = async (tenantId) => {
    const id = tenantId || getCurrentTenantId();
    if (!id) {
        return getDefaultPageConfig();
    }
    try {
        return await request(`/label/page-config/${id}`);
    } catch (error) {
        console.warn('Failed to load tenant page config:', error);
        return getDefaultPageConfig();
    }
};

/**
 * 获取租户信息
 */
export const getTenantInfo = async (tenantId) => {
    const id = tenantId || getCurrentTenantId();
    if (!id) {
        return null;
    }
    try {
        return await request(`/label/tenant/${id}`);
    } catch (error) {
        console.warn('Failed to load tenant info:', error);
        return null;
    }
};

/**
 * 获取租户配置（AI配额等）
 */
export const getTenantConfig = async (tenantId) => {
    const id = tenantId || getCurrentTenantId();
    if (!id) {
        return null;
    }
    try {
        return await request(`/label/tenant-config/tenant/${id}`);
    } catch (error) {
        console.warn('Failed to load tenant config:', error);
        return null;
    }
};

/**
 * 检查 AI 配额
 */
export const checkAiQuota = async (tenantId) => {
    const config = await getTenantConfig(tenantId);
    if (!config) {
        return { hasQuota: true, used: 0, total: 0 };
    }
    const used = config.aiUsedCount || 0;
    const total = config.aiQuota || 0;
    return {
        hasQuota: total === 0 || used < total, // total=0 表示无限制
        used,
        total,
        remaining: total > 0 ? total - used : -1,
    };
};

/**
 * 【安全修复】验证用户是否有权限访问指定租户
 * 切换租户前必须调用此接口验证
 */
export const validateTenantAccess = async (tenantId) => {
    if (!tenantId) {
        return { valid: false, message: '租户ID不能为空' };
    }
    try {
        const result = await request(`/label/tenant/${tenantId}/validate`);
        return {
            valid: result === true || result?.valid === true,
            message: result?.message || null,
        };
    } catch (error) {
        console.error('Tenant validation failed:', error);
        return {
            valid: false,
            message: error.message || '租户验证失败',
        };
    }
};

/**
 * 【安全修复】安全的租户切换（带后端验证）
 * 先验证权限再存储到 localStorage
 */
export const switchTenantWithValidation = async (tenantId, enterpriseId) => {
    // 1. 验证租户访问权限
    const validation = await validateTenantAccess(tenantId);
    if (!validation.valid) {
        throw new Error(validation.message || '无权访问该租户');
    }

    // 2. 验证通过，保存到本地
    setCurrentTenantId(tenantId);
    if (enterpriseId) {
        setCurrentEnterpriseId(enterpriseId);
    }

    return { success: true, tenantId, enterpriseId };
};

/**
 * 获取默认页面配置
 */
const getDefaultPageConfig = () => ({
    logo: null,
    tenantName: '食品数字标签系统',
    themeColor: '#2563eb',
    menuConfig: {
        showDashboard: true,
        showAudit: true,
        showStats: true,
    },
    layoutConfig: {
        sidebarWidth: 240,
        headerHeight: 64,
        showBreadcrumb: true,
        showFooter: false,
    },
});

export default {
    getCurrentTenantId,
    getCurrentEnterpriseId,
    setCurrentTenantId,
    setCurrentEnterpriseId,
    getTenantPageConfig,
    getTenantInfo,
    getTenantConfig,
    checkAiQuota,
    validateTenantAccess,
    switchTenantWithValidation,
};
